import { useEffect, useState } from 'react'
import { supabase } from '../../../../lib/supabase'

// Build last 6 months labels + date ranges dynamically
function getLast6Months() {
  const months = []
  const now = new Date()
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const start = new Date(d.getFullYear(), d.getMonth(), 1).toISOString()
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59).toISOString()
    months.push({
      label: d.toLocaleString('default', { month: 'short' }),
      start,
      end,
    })
  }
  return months
}

export default function MembershipChart() {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      const clubId = 'a0000000-0000-0000-0000-000000000001'
      const months = getLast6Months()

      const counts = await Promise.all(
        months.map(({ start, end }) =>
          supabase
            .from('supporters')
            .select('*', { count: 'exact', head: true })
            .eq('club_id', clubId)
            .gte('joined_at', start)
            .lte('joined_at', end)
        )
      )

      const result = months.map((m, i) => ({
        label: m.label,
        value: counts[i].count || 0,
      }))

      setData(result)
      setLoading(false)
    }
    fetchData()
  }, [])

  const max = Math.max(...data.map((d) => d.value), 1)
  const total = data.reduce((sum, d) => sum + d.value, 0)
  const lastMonth = data[data.length - 1]?.value || 0
  const prevMonth = data[data.length - 2]?.value || 0
  const trend = prevMonth > 0 ? Math.round(((lastMonth - prevMonth) / prevMonth) * 100) : null

  return (
    <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold">
            Monthly Registrations
          </div>
          {!loading && (
            <div className="text-2xl font-black text-gray-900 dark:text-white mt-1">
              {total} <span className="text-sm font-normal text-gray-500">past 6 months</span>
            </div>
          )}
        </div>
        {!loading && trend !== null && (
          <div className={`text-xs font-bold px-2 py-1 ${trend >= 0 ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/30 text-red-500'}`}>
            {trend >= 0 ? '▲' : '▼'} {Math.abs(trend)}% vs last month
          </div>
        )}
      </div>

      {loading ? (
        <div className="h-32 flex items-center justify-center text-gray-400 text-sm">Loading...</div>
      ) : (
        <div className="flex items-end gap-3 h-32">
          {data.map((item, i) => (
            <div key={item.label} className="flex-1 flex flex-col items-center gap-2">
              <div className="text-xs text-gray-500 font-semibold">{item.value > 0 ? item.value : ''}</div>
              <div
                className={`w-full transition-all ${
                  i === data.length - 1 ? 'bg-green-500' : 'bg-green-200 dark:bg-green-900/60'
                }`}
                style={{ height: `${(item.value / max) * 100}%`, minHeight: item.value > 0 ? '4px' : '0' }}
              />
              <div className="text-xs text-gray-500">{item.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}